import { useCallback, useEffect, useMemo, useState } from 'react';
import { compose } from 'recompose';
import { ChartFragment } from 'models/api-response';
import { FormFieldInputType } from 'models/enum';
import { ChartComponentProps, ChartMetaFormField } from 'models/ui';
import { chartHelper } from 'helpers';
import { chartService } from 'services';
import { withChartLogic } from 'components/common';
import { Title } from 'components/features/chart/special-charts/shared/constants';
import withSavedPatientChartsPage from 'components/features/shared/withSavedPatientChartsPage';
import {
  AnnualAmbulatoryFieldConfig,
  COLLECTION_CONFIGS,
  COLLECTION_GROUP_TITLE,
  CONDITIONAL_FIELDS,
  CollectionColumn,
  CollectionId,
  CollectionRow,
  FIELD_CONFIGS,
  FIELD_CONFIG_MAP,
  FormField,
  OTHER_OPTION_VALUE,
  STEP_CONFIGS,
  SectionTitle,
  VACCINE_CONFIGS,
  controllerFieldSatisfies,
  formatOtherCellValue
} from './constants';
import AnnualAmbulatoryCareView, { AnnualAmbulatoryCareViewProps } from './AnnualAmbulatoryCareView';

export interface AnnualAmbulatoryCareProps extends ChartComponentProps {
  enableMultiStepsAuthoring?: (isSupportMultiStepsAuthoring: boolean) => void;
  navigateToSavedPatientCharting: VoidFunction;
}

export type CollectionState = Record<CollectionId, CollectionRow[]>;

const LAST_STEP_INDEX = STEP_CONFIGS.length - 1;

const EMPTY_COLLECTIONS: CollectionState = {
  [CollectionId.ALLERGIES]: [],
  [CollectionId.SURGERIES]: [],
  [CollectionId.MEDICAL_PROBLEMS]: [],
  [CollectionId.YEARLY_GOALS]: []
};

// No field on this chart is mandatory (SIMSNG-6062) — authored content is legitimately partial.
const buildFormField = (fieldConfig: AnnualAmbulatoryFieldConfig): ChartMetaFormField => {
  const formField = chartHelper.createFormField({ name: fieldConfig.name, type: fieldConfig.type, label: fieldConfig.label });
  return { ...formField, isRequired: false, value: '' };
};

export const buildDefaultFormFields = (): Map<string, ChartMetaFormField> => {
  const dataMap = new Map<string, ChartMetaFormField>();
  FIELD_CONFIGS.forEach((fieldConfig) => dataMap.set(fieldConfig.name, buildFormField(fieldConfig)));
  return dataMap;
};

export const isConditionalFieldActive = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField): boolean => {
  const conditional = CONDITIONAL_FIELDS[fieldName];
  if (!conditional) return true;
  return controllerFieldSatisfies(formFieldMap.get(conditional.controller), conditional.whenValue);
};

export const serializeFieldValue = (value: unknown, type?: FormFieldInputType): string => {
  if (Array.isArray(value))
    return value
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join(', ');
  const raw = String(value ?? '').trim();
  if (type === FormFieldInputType.MULTISELECT_DROPDOWN) {
    return raw
      .split(/[|,]/)
      .map((part) => part.trim())
      .filter(Boolean)
      .join(', ');
  }
  return raw;
};

const OTHER_TEXT_FIELD_BY_CONTROLLER = new Map<FormField, FormField>();
Object.entries(CONDITIONAL_FIELDS).forEach(([otherField, conditional]) => {
  if (conditional && conditional.whenValue === OTHER_OPTION_VALUE) {
    OTHER_TEXT_FIELD_BY_CONTROLLER.set(conditional.controller, otherField as FormField);
  }
});
const OTHER_TEXT_FIELDS = new Set<FormField>(OTHER_TEXT_FIELD_BY_CONTROLLER.values());

const buildScalarRecord = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField) => {
  if (OTHER_TEXT_FIELDS.has(fieldName)) return null;
  const formField = formFieldMap.get(fieldName);
  if (!formField) return null;
  if (!isConditionalFieldActive(formFieldMap, fieldName)) return null;
  const fieldConfig = FIELD_CONFIG_MAP.get(fieldName);
  let stringValue = serializeFieldValue(formField.value, fieldConfig?.type);
  const otherField = OTHER_TEXT_FIELD_BY_CONTROLLER.get(fieldName);
  if (otherField) {
    const otherValue = serializeFieldValue(formFieldMap.get(otherField)?.value, FIELD_CONFIG_MAP.get(otherField)?.type);
    stringValue = formatOtherCellValue(stringValue, otherValue);
  }
  if (!stringValue) return null;
  // Labels may carry a manual line break for display (e.g. the abuse question); keep the saved title single-line.
  const title = (fieldConfig?.label || formField.label || '').replace(/\n/g, ' ');
  return chartService.buildPlainRecord({ formField: formField.name, title, content: stringValue, value: stringValue });
};

const PATIENT_INFO_STEP_TITLE = STEP_CONFIGS[0].title;
const PATIENT_INFO_COLUMN_HEADERS = { title: 'Details', content: 'Patient Information' };

const buildStepFieldRecords = (stepConfig: typeof STEP_CONFIGS[number], formFieldMap: Map<string, ChartMetaFormField>) =>
  stepConfig.sections.flatMap((section) => section.rows.flatMap((row) => row.map((fieldName) => buildScalarRecord(formFieldMap, fieldName))));

const IMMUNIZATIONS_STEP_TITLE = STEP_CONFIGS[2].title;

const serializeField = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField): string =>
  serializeFieldValue(formFieldMap.get(fieldName)?.value, FIELD_CONFIG_MAP.get(fieldName)?.type);

const buildImmunizationRow = (formFieldMap: Map<string, ChartMetaFormField>, fieldName: FormField, title: string) => {
  const value = serializeField(formFieldMap, fieldName);
  return chartService.buildPlainRecord({ formField: fieldName, title, content: value, value });
};

const buildImmunizationSection = (formFieldMap: Map<string, ChartMetaFormField>, label: string, statusField: FormField, dateField: FormField, boosterField: FormField) => {
  const hasData = [statusField, dateField, boosterField].some((fieldName) => serializeField(formFieldMap, fieldName));
  if (!hasData) return null;
  return {
    // Vaccine labels already end with ':'; only append one for names that don't (e.g. "Other") to avoid a double colon.
    sectionTitle: label.endsWith(':') ? label : `${label}:`,
    groupTitle: IMMUNIZATIONS_STEP_TITLE,
    useSectionTitleAsHeader: true,
    records: [
      buildImmunizationRow(formFieldMap, statusField, 'Status'),
      buildImmunizationRow(formFieldMap, dateField, 'Date'),
      buildImmunizationRow(formFieldMap, boosterField, 'Desire Booster')
    ]
  };
};

const buildImmunizationSections = (formFieldMap: Map<string, ChartMetaFormField>) => {
  const vaccineSections = VACCINE_CONFIGS.map((vaccine) => buildImmunizationSection(formFieldMap, vaccine.label, vaccine.statusField, vaccine.dateField, vaccine.boosterField));
  const otherName = serializeField(formFieldMap, FormField.IMM_OTHER_IMMUNIZATION);
  const otherSection = buildImmunizationSection(formFieldMap, otherName || 'Other', FormField.IMM_OTHER_STATUS, FormField.IMM_OTHER_DATE, FormField.IMM_OTHER_BOOSTER);
  return [...vaccineSections, otherSection].filter(Boolean);
};

const LIVING_STEP_TITLE = STEP_CONFIGS[3].title;
const EXAMINING_PROVIDER_COLUMN_HEADERS = { title: 'Provider', content: 'Details' };
const EXAMINING_PROVIDER_FIELDS = [FormField.EXAMINING_PROVIDER, FormField.EXAMINING_TIME];

const sectionHasExaminingProvider = (section: { rows: FormField[][] }): boolean => section.rows.some((row) => row.includes(FormField.EXAMINING_PROVIDER));

const buildExaminingProviderSection = (formFieldMap: Map<string, ChartMetaFormField>) => ({
  sectionTitle: '',
  groupTitle: LIVING_STEP_TITLE,
  columnHeaders: EXAMINING_PROVIDER_COLUMN_HEADERS,
  records: EXAMINING_PROVIDER_FIELDS.map((fieldName) => buildScalarRecord(formFieldMap, fieldName))
});

const buildScalarSections = (formFieldMap: Map<string, ChartMetaFormField>) =>
  STEP_CONFIGS.flatMap((stepConfig) => {
    if (stepConfig.title === PATIENT_INFO_STEP_TITLE) {
      return [
        {
          sectionTitle: stepConfig.title,
          groupTitle: stepConfig.title,
          columnHeaders: PATIENT_INFO_COLUMN_HEADERS,
          records: buildStepFieldRecords(stepConfig, formFieldMap)
        }
      ];
    }
    if (stepConfig.title === IMMUNIZATIONS_STEP_TITLE) {
      return buildImmunizationSections(formFieldMap);
    }
    // Examining Provider is appended after the collections (see buildSavedChartSections), so skip
    // it here to keep it below Yearly Health Goals within the group.
    return stepConfig.sections
      .filter((section) => !sectionHasExaminingProvider(section))
      .map((section) => ({
        sectionTitle: section.title,
        groupTitle: stepConfig.title,
        records: section.rows.flatMap((row) => row.map((fieldName) => buildScalarRecord(formFieldMap, fieldName)))
      }));
  });

const collectionCellValue = (rowData: CollectionRow, column: CollectionColumn): string => {
  const base = String(rowData[column.field] ?? '').trim();
  const otherValue = column.otherField ? String(rowData[column.otherField] ?? '').trim() : '';
  return formatOtherCellValue(base, otherValue);
};

const buildCollectionSections = (collections: CollectionState) =>
  Object.values(COLLECTION_CONFIGS).map((config) => ({
    sectionTitle: config.title,
    groupTitle: COLLECTION_GROUP_TITLE[config.id],
    tableColumns: config.columns.map((column) => column.header),
    tableRows: collections[config.id].map((rowData) => config.columns.map((column) => collectionCellValue(rowData, column))),
    records: collections[config.id].flatMap((rowData, rowIndex) =>
      config.columns.map((column) => {
        const cellValue = collectionCellValue(rowData, column);
        if (!cellValue) return null;
        return chartService.buildPlainRecord({ formField: `${config.id}-${rowIndex}-${column.field}`, title: column.header, content: cellValue, value: cellValue });
      })
    )
  }));

export const buildSavedChartSections = (formFieldMap: Map<string, ChartMetaFormField>, collections: CollectionState) => [
  ...buildScalarSections(formFieldMap),
  ...buildCollectionSections(collections),
  buildExaminingProviderSection(formFieldMap)
];

export const computeCompletedSteps = (furthestStep: number): boolean[] => STEP_CONFIGS.map((_, stepIndex) => stepIndex < furthestStep);

const resolveFormFieldMap = (formFieldMap?: Map<string, ChartMetaFormField>): Map<string, ChartMetaFormField> => formFieldMap || buildDefaultFormFields();

const canSelectStep = (stepIndex: number, furthestStep: number, isAuthor: boolean): boolean => !isAuthor || stepIndex <= furthestStep;

const AnnualAmbulatoryCare = (props: AnnualAmbulatoryCareProps) => {
  const {
    chartingTime,
    displayAuthoringData,
    enableDisplayRecordsButton,
    enableMultiStepsAuthoring,
    formFieldMap,
    formSubmittedCount,
    initState,
    navigateToSavedPatientCharting,
    resetFormSubmitted,
    saveChart
  } = props;

  const [activeStep, setActiveStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);
  const [collections, setCollections] = useState<CollectionState>(EMPTY_COLLECTIONS);
  const safeFormFieldMap = useMemo(() => resolveFormFieldMap(formFieldMap), [formFieldMap]);

  useEffect(() => {
    initState(buildDefaultFormFields());
    if (enableMultiStepsAuthoring) enableMultiStepsAuthoring(true);
  }, [enableMultiStepsAuthoring, initState]);

  const completedSteps = computeCompletedSteps(furthestStep);

  const replaceCollection = useCallback((collectionId: CollectionId, rows: CollectionRow[]) => {
    setCollections((prev) => ({ ...prev, [collectionId]: rows }));
  }, []);

  // --- Fragment building ------------------------------------------------------

  const buildFragment = useCallback((): ChartFragment => {
    const record = {
      chartTitle: Title.SPECIAL_CHARTS,
      fragmentTitle: SectionTitle.ANNUAL_AMBULATORY_CARE,
      records: buildSavedChartSections(safeFormFieldMap, collections)
    };
    const cleanRecord = chartService.systemAssessment.removeEmptyRecords(record);
    const basicInfo = chartService.createBaseFragment({ chartingTime });
    return { ...basicInfo, chartData: cleanRecord } as ChartFragment;
  }, [chartingTime, collections, safeFormFieldMap]);

  const handleAfterSave = useCallback(() => {
    if (!props.isAuthor) navigateToSavedPatientCharting();
  }, [navigateToSavedPatientCharting, props.isAuthor]);

  const handleSaveClick = useCallback(() => {
    saveChart([buildFragment()], { afterSave: handleAfterSave });
  }, [buildFragment, handleAfterSave, saveChart]);

  // --- Step navigation --------------------------------------------------------

  const handleResetSubmitted = useCallback(() => {
    if (resetFormSubmitted) resetFormSubmitted();
  }, [resetFormSubmitted]);

  const handleSelectStep = useCallback(
    (stepIndex: number) => {
      if (!canSelectStep(stepIndex, furthestStep, Boolean(props.isAuthor))) return;
      setActiveStep(stepIndex);
      handleResetSubmitted();
    },
    [furthestStep, handleResetSubmitted, props.isAuthor]
  );

  const handleBackStep = useCallback(() => {
    if (activeStep === 0) return;
    setActiveStep(activeStep - 1);
    handleResetSubmitted();
  }, [activeStep, handleResetSubmitted]);

  const handleContinueStep = useCallback(() => {
    if (activeStep === LAST_STEP_INDEX) {
      handleSaveClick();
      return;
    }
    const nextStep = activeStep + 1;
    setFurthestStep((previousStep) => Math.max(previousStep, nextStep));
    setActiveStep(nextStep);
    handleResetSubmitted();
  }, [activeStep, handleResetSubmitted, handleSaveClick]);

  const viewProps: AnnualAmbulatoryCareViewProps = {
    activeStep,
    collections,
    completedSteps,
    displayAuthoringData,
    enableDisplayRecordsButton: Boolean(enableDisplayRecordsButton),
    formFieldMap: safeFormFieldMap,
    formSubmittedCount,
    furthestStep,
    isAuthor: Boolean(props.isAuthor),
    onBackStep: handleBackStep,
    onContinueStep: handleContinueStep,
    onReplaceCollection: replaceCollection,
    onSelectStep: handleSelectStep
  };

  return <AnnualAmbulatoryCareView {...viewProps} />;
};

export { AnnualAmbulatoryCare as BaseAnnualAmbulatoryCare };
export default compose(withSavedPatientChartsPage, withChartLogic)(AnnualAmbulatoryCare);
